// useArrivalDetector — watches the released-audios list and surfaces a
// newly-arrived audio so the host can render <ArrivalOverlay />.
//
// Persists "last seen releasedAt" to localStorage so re-opening the PWA
// (which re-mounts React and resets in-memory refs) doesn't re-fire the
// arrival overlay for audios the learner has already seen.
//
// First-ever load (no localStorage key) records the current max as baseline
// and does NOT fire — otherwise every brand-new install would flash overlay
// for the most recently released audio.

import type { AudioDoc, WithId } from "@goodbye/firebase";
import { useEffect, useState } from "react";

const LS_KEY = "goodbye:lastSeenReleasedAt";

function readLastSeen(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (raw === null) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeLastSeen(ms: number): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LS_KEY, String(ms));
  } catch {
    /* localStorage unavailable */
  }
}

function releasedAtMs(a: WithId<AudioDoc>): number {
  return a.releasedAt?.toMillis?.() ?? 0;
}

export function useArrivalDetector(
  audios: WithId<AudioDoc>[],
): {
  arrival: WithId<AudioDoc> | null;
  dismiss: () => void;
  markSeen: (a: WithId<AudioDoc>) => void;
} {
  const [arrival, setArrival] = useState<WithId<AudioDoc> | null>(null);

  useEffect(() => {
    if (audios.length === 0) return;

    const newest = audios[0];
    if (!newest) return;
    const newestMs = releasedAtMs(newest);
    if (newestMs === 0) return;

    const lastSeen = readLastSeen();

    if (lastSeen === null) {
      writeLastSeen(newestMs);
      return;
    }

    if (newestMs > lastSeen) {
      setArrival(newest);
    }
  }, [audios]);

  const markSeen = (a: WithId<AudioDoc>) => {
    const ms = releasedAtMs(a);
    if (ms > 0) {
      const cur = readLastSeen() ?? 0;
      if (ms > cur) writeLastSeen(ms);
    }
  };

  const dismiss = () => {
    if (arrival) markSeen(arrival);
    setArrival(null);
  };

  return { arrival, dismiss, markSeen };
}
